<template>
<table class="dashboard-list">
    <thead>
        <tr>
            <th class="dl-th-cover"></th>
            <th class="dl-th-pin"></th>
            <th class="dl-th-name" :aria-sort="ariaSort('name')">
                <button type="button" class="dl-sort" @click="sortBy('name')">
                    Name <span class="dl-arrow" v-if="sort.key === 'name'">{{ sort.dir === 'asc' ? '▲' : '▼' }}</span>
                </button>
            </th>
            <th class="dl-th-date" :aria-sort="ariaSort('date')">
                <button type="button" class="dl-sort" @click="sortBy('date')">
                    Last Updated <span class="dl-arrow" v-if="sort.key === 'date'">{{ sort.dir === 'asc' ? '▲' : '▼' }}</span>
                </button>
            </th>
            <th class="dl-th-owner">Owner</th>
            <th class="dl-th-actions"></th>
        </tr>
    </thead>
    <tbody>
        <dashboard-list-row
            v-for="project in projects"
            :key="project.id"
            :project="project"
            @projectAccepted="$emit('projectAccepted', $event)"
            @projectDeleted="$emit('projectDeleted', $event)"
        />
    </tbody>
</table>
</template>

<script>
/**
 * List (table) view of a project section. Renders the sortable column headers
 * (shared sort model, emits sortChange) and delegates each row to a keyed
 * dashboard-list-row component that owns its own image/nav state.
 */
import DashboardListRow from '@/components/dashboard/dashboard-list-row';

const DashboardList = {
    name: 'DashboardList',
    props: {
        projects: { type: Array, default: () => [] },
        sort: { type: Object, default: () => ({ key: 'date', dir: 'desc' }) }
    },
    methods: {
        ariaSort(key) {
            if (this.sort.key !== key) return 'none';
            return this.sort.dir === 'asc' ? 'ascending' : 'descending';
        },
        sortBy(key) {
            this.$emit('sortChange', key);
        }
    },
    components: {
        'dashboard-list-row': DashboardListRow
    }
};
export default DashboardList;
</script>

<style lang="scss">
.dashboard-list {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;

    thead th {
        text-align: left;
        padding: 8px 12px;
        border-bottom: 2px solid rgba(0, 0, 0, 0.12);
        color: #333;
        font-weight: 600;
        .dl-sort {
            background: none;
            border: none;
            padding: 0;
            font: inherit;
            font-weight: 600;
            cursor: pointer;
            color: inherit;
            // Keep the label and its sort arrow on one line. Without this the
            // arrow wrapped under "Last Updated" whenever the column happened to
            // be narrow.
            white-space: nowrap;
            &:focus { outline: 2px solid var(--accent); outline-offset: 2px; }
        }
        .dl-arrow { color: var(--accent); font-size: 11px; }
    }
    // Fixed width so a column of only <img> covers doesn't collapse (an <img>
    // reserves no table-layout space until it loads / without width attrs).
    .dl-th-cover { width: 60px; }
    .dl-th-pin, .dl-th-actions { width: 1%; }
    // Each section (Pinned, Recent, …) is its own <table>, so with auto layout
    // every section sized its columns from its own rows — a section holding
    // longer owner names or titles squeezed the date column, which is what
    // pushed the sort arrow onto a second line and left the sections visually
    // out of register with each other. Pinning the date and owner widths makes
    // every section lay out identically, with Name taking the remainder.
    .dl-th-date { width: 132px; }
    .dl-th-owner { width: 160px; }

    tbody .dl-row {
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        td { padding: 8px 12px; vertical-align: middle; cursor: pointer; }
        .dl-cover {
            width: 60px;
            img, .dl-generated {
                display: block;
                width: 36px; height: 36px;
                border-radius: 4px;
                object-fit: cover;
            }
            .dl-generated {
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                font-weight: 700;
                font-size: 14px;
            }
        }
        .dl-pin { cursor: default; }
        .dl-pin-btn {
            background: none; border: none; padding: 2px; cursor: pointer;
            i { font-size: 18px !important; color: #b0b0b0; transform: rotate(45deg); }
            &.is-pinned i { color: var(--accent); transform: rotate(0deg); }
            &:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
        }
        .dl-name {
            font-weight: 600;
            color: #333;
            max-width: 360px;
            // Title and Show Pass badge share one line: the title truncates to
            // make room instead of pushing the badge onto a second line, where
            // its left margin read as a stray indent that lined up with nothing.
            .dl-name-inner {
                display: flex;
                align-items: center;
                gap: 8px;
                min-width: 0;
            }
            .dl-name-text {
                flex: 0 1 auto;
                min-width: 0; // lets the flex item shrink so the ellipsis engages
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }
        .dl-date, .dl-owner { color: #666; }
        .dl-actions { cursor: default; text-align: right; }
    }
}
.md-theme-show-mode {
    .dashboard-list {
        thead th { color: rgba(255, 255, 255, 0.87); border-bottom-color: rgba(255, 255, 255, 0.2); }
        tbody .dl-row {
            border-bottom-color: rgba(255, 255, 255, 0.1);
            .dl-name { color: rgba(255, 255, 255, 0.92); }
            .dl-date, .dl-owner { color: rgba(255, 255, 255, 0.7); }
            .dl-pin-btn i { color: #90a4ae; }
            .dl-pin-btn.is-pinned i { color: var(--accent); }
        }
    }
}
</style>


// WEBPACK FOOTER //
// dashboard-list.vue?9cc4eb1a